import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import CustomOrderPannel, {
  DetailsPillsSection,
  DescriptionSection,
  getListingProperties,
} from '../../components/CustomOrderPanel/CustomOrderPannel.jsx';
import axios from 'axios';
import { apiBaseUrl } from '../../util/api.js';
import { addToLikeList, incrementCartCount, userLikeCount } from '../../ducks/user.duck.js';
import { trackViewContent, trackAddToWishlist } from '../../util/metaPixel';
import { trackAddToWishlist as trackGA4AddToWishlist } from '../../util/googleAnalytics';
import BackButton from '../../components/Button/BackButton.js';
import KKLoader from '../../components/KKLoader/KKLoader.js';
import ListingPageSkeleton from '../../components/ListingPageSkeleton/ListingPageSkeleton.js';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { setListingLikedState } from '../SearchPage/SearchPage.duck';
import { dripPush } from '../../util/drip.js';
import { generateSeoTitle, generateProductSchema } from '../../util/seoProductSchema';
import ListingPageSimilarSections from './ListingPageSimilarSections/ListingPageSimilarSections';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const formatBrandName = brand => {
  if (!brand) return '';
  let formatted = brand.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  return formatted
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onAddOneUserCartCount,
    fetchAllSuccessInProgress,
    listingId: listingIdFromProps,
    fetchListingIdInProgress,
    fetchAllSuccess,
    favroiteListingIds,
    moreLikeThis,
    moreFromSeller,
    onAddList,
    ...restOfProps
  } = props;
  const { Money } = sdkTypes;
  const listingConfig = config.listing;
  const listingId = rawParams.id ? new UUID(rawParams.id) : listingIdFromProps;
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const initialLikedFromRedux = Boolean(
    favroiteListingIds && listingId?.uuid && favroiteListingIds.has(listingId.uuid)
  );
  const [mounted, setMounted] = useState(false);
  // Like state is only revealed once the server check resolves, so we never
  // show a guessed value that then flickers to the real one.
  const [likeStatusLoaded, setLikeStatusLoaded] = useState(false);
  const [like, setLike] = useState(initialLikedFromRedux);
  const [isInitialLiked, setIsInitialLiked] = useState(initialLikedFromRedux);
  const targetLikeRef = useRef(initialLikedFromRedux);
  const serverLikedStateRef = useRef(initialLikedFromRedux);
  const isSyncingRef = useRef(false);
  const isMountedRef = useRef(true);
  const dispatch = useDispatch();

  const checkLiked = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl()}/api/likes/check?ids=${listingId?.uuid}`,
        { withCredentials: true },
        { userId: currentUser?.id?.uuid }
      );
      const isLikedOnServer = Boolean(response?.data?.data?.length > 0);
      serverLikedStateRef.current = isLikedOnServer;
      targetLikeRef.current = isLikedOnServer;
      setIsInitialLiked(isLikedOnServer);
      setLike(isLikedOnServer);
      setLikeStatusLoaded(true);
    } catch (error) {
      console.log(error);
      setLikeStatusLoaded(true);
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    const nextInitial = Boolean(
      favroiteListingIds && listingId?.uuid && favroiteListingIds.has(listingId.uuid)
    );
    // Reset to a neutral, not-yet-loaded state when the listing changes so we
    // never briefly show the previous listing's like status; checkLiked below
    // will reveal the real value for this listing in one go.
    setLikeStatusLoaded(false);
    setIsInitialLiked(nextInitial);
    setLike(nextInitial);
    targetLikeRef.current = nextInitial;
    serverLikedStateRef.current = nextInitial;
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [listingId?.uuid]);

  useEffect(() => {
    setMounted(true);
    checkLiked();
  }, [listingId?.uuid, currentUser?.id?.uuid]);

  useEffect(() => {
    // Track listing view with Meta Pixel when listing data is available
    if (currentListing.id) {
      trackViewContent(currentListing);
    }

    // Increment listing view count (buffered on server & tracked in Drip)
    if (currentListing.id) {
      axios
        .post(
          `${apiBaseUrl()}/api/track/view`,
          {
            listingId: currentListing.id.uuid,
            authorId: currentListing.author?.id?.uuid,
            userId: props.currentUser?.id?.uuid,
          },
          { withCredentials: true }
        )
        .catch(() => {});
    }

    // Drip: Viewed a Product event via Drip JS Snippet
    if (currentListing.id) {
      const listingPrice = currentListing.attributes?.price;
      const priceValue = listingPrice?.amount ? listingPrice.amount / 100 : 0;
      const firstImg = currentListing.images?.[0];
      const imageUrl =
        firstImg?.attributes?.variants?.['landscape-crop']?.url ||
        firstImg?.attributes?.variants?.['default']?.url ||
        '';
      const publicData = currentListing.attributes?.publicData || {};

      const categoryList = [publicData.categoryLevel1, publicData.categoryLevel2].filter(Boolean);
      const categories =
        categoryList.length > 0 ? categoryList : publicData.category ? [publicData.category] : [];
      const brand = formatBrandName(publicData.brand || publicData.brandName || '');
      const size =
        publicData.size ||
        publicData.clothingSize ||
        publicData.shoeSize ||
        publicData.sizeOption ||
        '';

      dripPush([
        'track',
        'Viewed a Product',
        {
          product_id: currentListing.id.uuid,
          product_variant_id: currentListing.id.uuid,
          name: currentListing.attributes?.title || '',
          categories: categories,
          price: priceValue,
          currency: 'R',
          product_url: typeof window !== 'undefined' ? window.location.href : '',
          image_url: imageUrl,
          brand: brand,
          size: size,
          source: 'kloset_klub',
        },
      ]);
    }

    // Track listing view with Google Analytics 4 (view_item event)
    if (currentListing.id && window.gtag) {
      const listingPrice = currentListing.attributes.price;
      const priceValue = listingPrice?.amount ? listingPrice.amount / 100 : 0;
      const currency = listingPrice?.currency || 'ZAR';

      window.gtag('event', 'view_item', {
        currency: currency,
        value: priceValue,
        items: [
          {
            item_id: currentListing.id.uuid,
            item_name: currentListing.attributes.title || 'Listing',
            price: priceValue,
            currency: currency,
          },
        ],
      });
    }
  }, [currentListing.id?.uuid]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  // Prepare basic meta for errors and loading states
  const marketplaceName = config.marketplaceName;
  const basicTitle = currentListing?.attributes?.title || '';
  const basicDescription = currentListing?.attributes?.description || '';

  // Use marketplace name as fallback for better social previews
  const marketplaceDescription = intl.formatMessage({ id: 'Page.schemaDescription' });
  const socialPreviewTitle = basicTitle || marketplaceName;
  const socialPreviewDescription = basicDescription || marketplaceDescription;

  const basicPageTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title: basicTitle || 'Listing', price: '', marketplaceName }
  );

  // Show loader while listing is being fetched - prioritize loading state over errors
  // This prevents flashing incomplete UI or error stubs during initial load
  // Also require title to avoid rendering with empty attributes
  // Only show loader when we genuinely have no listing data yet.
  // Removing `fetchAllSuccessInProgress` prevents a loader flash during the
  // client-side re-fetch that follows SSR hydration.
  const hasListingData = currentListing.id && basicTitle;
  const showInitialLoader = !hasListingData && fetchListingIdInProgress;

  if (showInitialLoader || !currentListing.id || !basicTitle) {
    return (
      <Page
        title={basicPageTitle}
        description={socialPreviewDescription}
        socialSharing={{ title: socialPreviewTitle, description: socialPreviewDescription }}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          name: socialPreviewTitle,
          description: socialPreviewDescription,
        }}
      >
        <LayoutSingleColumn className={css.pageRoot} topbar={topbar}>
          <ListingPageSkeleton />
        </LayoutSingleColumn>
      </Page>
    );
  }

  // Handle errors only after we've confirmed we're not loading
  // This ensures errors are shown only when data loading is truly complete
  if (showListingError && showListingError.status === 404) {
    const errorImages = listingImages(currentListing, 'facebook');
    return (
      <Page
        title={basicPageTitle}
        description={socialPreviewDescription}
        facebookImages={errorImages}
        twitterImages={errorImages}
        socialSharing={{
          title: socialPreviewTitle,
          description: socialPreviewDescription,
        }}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          name: socialPreviewTitle,
          description: socialPreviewDescription,
          image: errorImages.map(img => img.url),
        }}
      >
        <NotFoundPage staticContext={props.staticContext} />
      </Page>
    );
  } else if (showListingError && fetchAllSuccess) {
    const errorImages = listingImages(currentListing, 'facebook');
    return (
      <Page
        title={basicPageTitle}
        description={socialPreviewDescription}
        facebookImages={errorImages}
        twitterImages={errorImages}
        socialSharing={{
          title: socialPreviewTitle,
          description: socialPreviewDescription,
        }}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          name: socialPreviewTitle,
          description: socialPreviewDescription,
          image: errorImages.map(img => img.url),
        }}
      >
        <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />
      </Page>
    );
  }

  // Now that listing is loaded, prepare complete meta tags with images
  const listingTitle = currentListing.attributes.title;
  const listingDescription = currentListing.attributes.description || '';
  const listingFacebookImages = listingImages(currentListing, 'facebook');
  const listingTwitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);

  const pageTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title: listingTitle, price: '', marketplaceName }
  );

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;

  // Only show error page if listing data is loaded but missing required fields
  // if (!(listingType && transactionProcessAlias && unitType)) {
  //   // Listing should always contain listingType, transactionProcessAlias and unitType)
  //   return (
  //     <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
  //   );
  // }
  const processName = resolveLatestProcessName(transactionProcessAlias?.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    processType !== 'inquiry' &&
    !currentUser?.attributes?.profile?.protectedData?.tradesafeToken;
  // const noPayoutDetailsSetWithOwnListing =
  //   isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;

  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="AccountDetailsPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
    listingId: listingIdFromProps?.uuid,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    listingId: listingIdFromProps?.uuid,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  // const schemaImages = listingImages(
  //   currentListing,
  //   `${config.layout.listingImage.variantPrefix}-2x`
  // ).map(img => img.url);
  const seoTitle = generateSeoTitle(currentListing, config);
  const productSchema = generateProductSchema(currentListing, config, location);

  const listingIdUuid = currentListing?.id?.uuid;
  const listingPrice = currentListing?.attributes?.price;
  const priceValue = listingPrice?.amount != null ? (listingPrice.amount / 100).toFixed(2) : null;
  const priceCurrency = listingPrice?.currency || 'ZAR';
  const isClosed = currentListing?.attributes?.state === LISTING_STATE_CLOSED;
  const availability = isClosed ? 'out of stock' : 'in stock';
  const productCondition = publicData?.condition
    ? ['new', 'brand new'].includes(String(publicData.condition).toLowerCase())
      ? 'new'
      : 'used'
    : 'used';
  const productBrand = publicData?.brand || publicData?.brandName || '';
  const productCategory = publicData?.categoryLevel1 || publicData?.category || '';

  const metaProductData = listingIdUuid
    ? {
        retailerItemId: listingIdUuid,
        priceAmount: priceValue,
        priceCurrency,
        availability,
        condition: productCondition,
        brand: productBrand,
        category: productCategory,
      }
    : null;

  const syncLikeWithServer = async () => {
    if (isSyncingRef.current) return;
    if (targetLikeRef.current === serverLikedStateRef.current) return;

    isSyncingRef.current = true;

    const rawLikes =
      currentListing?.attributes?.metadata?.likesCount ??
      currentListing?.attributes?.publicData?.likesCount ??
      0;
    const baseLikes = typeof rawLikes === 'number' ? rawLikes : Number(rawLikes) || 0;
    const initialLikedBool = Boolean(isInitialLiked);

    try {
      while (targetLikeRef.current !== serverLikedStateRef.current) {
        const response = await axios.post(
          `${apiBaseUrl()}/api/likes/add`,
          {
            listingId: listingId?.uuid,
            authorId: currentAuthor?.id?.uuid,
            userId: currentUser?.id?.uuid,
          },
          {
            withCredentials: true,
          }
        );

        if (response?.data?.message === 'Like added successfully') {
          serverLikedStateRef.current = true;
        } else if (response?.data?.message) {
          serverLikedStateRef.current = false;
        } else {
          serverLikedStateRef.current = !serverLikedStateRef.current;
        }

        // Keep the cross-page favorites set and count in sync so other mounted/cached
        // pages (e.g. search page) reflect this like/dislike immediately.
        const currentCount = Math.max(
          0,
          baseLikes +
            (serverLikedStateRef.current ? (initialLikedBool ? 0 : 1) : initialLikedBool ? -1 : 0)
        );
        dispatch(setListingLikedState(listingId?.uuid, serverLikedStateRef.current, currentCount));
        userLikeCount(dispatch);
      }
    } catch (error) {
      console.error('Failed to sync like status with server:', error);
      targetLikeRef.current = serverLikedStateRef.current;
      if (isMountedRef.current) setLike(serverLikedStateRef.current);
      const currentCount = Math.max(
        0,
        baseLikes +
          (serverLikedStateRef.current ? (initialLikedBool ? 0 : 1) : initialLikedBool ? -1 : 0)
      );
      dispatch(setListingLikedState(listingId?.uuid, serverLikedStateRef.current, currentCount));
    } finally {
      isSyncingRef.current = false;
      if (isMountedRef.current) setLike(targetLikeRef.current);
    }
  };

  const handelWishlist = () => {
    if (!currentUser) {
      return history.push('/login');
    }

    const nextState = !targetLikeRef.current;
    targetLikeRef.current = nextState;

    // 1. Instant local UI feedback
    setLike(nextState);
    if (nextState) {
      trackAddToWishlist(currentListing);
      trackGA4AddToWishlist(currentListing, currentUser?.id?.uuid);
    }

    // 2. Sync with server immediately (no debounce) so the count/heart never
    // lags behind or gets dropped by a quick navigation away.
    syncLikeWithServer();
  };

  // if (!mounted && fetchAllSuccessInProgress) {
  //   return (
  //     <KKLoader
  //       fullscreen={true}
  //       showLogo={true}
  //       variant={'light'}
  //       title={intl.formatMessage({ id: 'ListingPage.loadingListingTitle', defaultMessage: 'Loading listing…' })}
  //       subtitle={intl.formatMessage({ id: 'ListingPage.loadingListingSubtitle', defaultMessage: 'Please wait...' })}
  //     />
  //   );
  // }

  return (
    <Page
      title={seoTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={productSchema}
      openGraphType="product"
      productData={metaProductData}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        mainColumnClassName={css.mainColumn}
        topbar={topbar}
        footer={<FooterContainer floatingBarHeight={{ mobile: 40, desktop: 90 }} />}
      >
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForHeroLayoutNew}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={classNames(css.onlyForPayoutDetails, css.actionBarForProductLayout)}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {/* <SectionTextMaybe text={description} showAsIngress isRich /> */}

            {/* <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            /> */}

            {/* <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            /> */}
            {/* <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} /> */}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
              pageType="listing Details"
              handelWishlist={handelWishlist}
              like={like}
              isInitialLiked={isInitialLiked}
              likeStatusLoaded={likeStatusLoaded}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              isOwnListing={isOwnListing}
            />

            {/* Desktop Left Column: Title, Details, What You Should Know */}
            <div className={css.desktopLeftColumnDetails}>
              <h1 className={css.desktopProductTitle}>{richTitle}</h1>
              <DetailsPillsSection propertise={getListingProperties(currentListing, config)} />
              <DescriptionSection description={currentListing?.attributes?.description} />
            </div>
          </div>
          <div className={css.orderColumnForHeroLayoutNew}>
            {/* <OrderPanel
              className={css.productOrderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
            /> */}
            <CustomOrderPannel
              className={css.productOrderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={setInquiryModalOpen}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              newTitle={title}
              authorDisplayName={authorDisplayName}
              onAddOneUserCartCount={onAddOneUserCartCount}
              pageType="listing Details"
              routeConfiguration={routeConfiguration}
            />
          </div>
        </div>
        {currentListing?.id ? (
          <ListingPageSimilarSections
            listing={currentListing}
            moreLikeThis={moreLikeThis}
            moreFromSeller={moreFromSeller}
            history={history}
            favroiteListingIds={favroiteListingIds}
            onAddList={onAddList}
            dispatch={dispatch}
            config={config}
          />
        ) : null}
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    id,
    fetchListingIdInProgress,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    fetchAllSuccessInProgress,
    fetchAllSuccess,
  } = state.ListingPage;
  const { currentUser, currentUserCartCount } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    listingId: id,
    fetchListingIdInProgress,
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    currentUserCartCount,
    fetchAllSuccessInProgress,
    fetchAllSuccess,
    favroiteListingIds: state.SearchPage?.favroiteListingIds,
    moreLikeThis: state.ListingPage?.moreLikeThis,
    moreFromSeller: state.ListingPage?.moreFromSeller,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel(
  onAddOneUserCartCount: () => dispatch(incrementCartCount()),
  onAddList: (listingId, authorId) => dispatch(addToLikeList(listingId, authorId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
